import { dateFromISOString } from '@wz-utilities/date.utilities';
import { clearStyleTags } from '@wz-utilities/sanitize.utilities';
import { EmailApiModels } from 'models/api/email-api.models';
import { EmailModels } from 'models/email.models';
import { HealthModels } from 'models/health.models';
import { AgencyModels } from '../../../../models/agency.models';
import { AgencyApiModels } from '../../../../models/api/agency-api.models';
import { TableModels } from '../../../../models/table.models';
import { projectPaginatedResult } from './table-projectors';

export function projectAgencySlipTableRow(
  api: AgencyApiModels.AgencySlipTableRow,
): AgencyModels.AgencySlipTableRow {
  return {
    agencyId: api.agencyId,
    creationDate: dateFromISOString(api.creationDate) ?? new Date(),
    fileUrl: api.fileUrl,
    excelFileUrl: api.excelFileUrl,
    id: api.id,
    isPaid: api.isPaid,
    insuranceTypeId: api.insuranceTypeId,
  };
}

export function projectAgencyTable(
  apiModel: TableModels.PaginatedResult<AgencyApiModels.AgencyTableRow>,
): TableModels.PaginatedResult<AgencyModels.AgencyTableRow> {
  return {
    result: apiModel.result.map((res) => {
      const row: AgencyModels.AgencyTableRow = {
        agencyId: res.agencyId,
        companyType: res.companyType,
        creationDate: dateFromISOString(res.creationDate) ?? new Date(),
        headOfAgency: res.headOfAgency,
        name: res.name,
        publicId: res.publicId,
        state: res.state,
      };

      return row;
    }),
    resultCount: apiModel.resultCount,
  };
}

export function projectBrokerTable(
  api: TableModels.PaginatedResult<AgencyApiModels.SubBrokerTableRow>,
): TableModels.PaginatedResult<AgencyModels.SubBrokerTableRow> {
  return projectPaginatedResult<
    AgencyApiModels.SubBrokerTableRow,
    AgencyModels.SubBrokerTableRow
  >(api, (b) => ({
    subBrokerId: b.id,
    login: b.login,
    creationDate: dateFromISOString(b.creationDate) ?? new Date(),
    policiesCount: b.policiesCount,
    handling: b.handling,
  }));
}

export function projectSubscriptionTable(
  apiModel: TableModels.PaginatedResult<AgencyApiModels.SubscriptionTableRow>,
): TableModels.PaginatedResult<AgencyModels.SubscriptionTableRow> {
  return {
    result: apiModel.result.map((res) => {
      const row: AgencyModels.SubscriptionTableRow = {
        agencyId: res.agencyId,
        creationDate: dateFromISOString(res.creationDate) ?? new Date(),
        name: res.name,
        contact: res.contact,
        kbis: res.kbis,
        orias: res.orias,
        rib: res.rib,
      };

      return row;
    }),
    resultCount: apiModel.resultCount,
  };
}

export function projectEmailTable(
  apiModel: TableModels.PaginatedResult<EmailApiModels.EmailTableRow>,
): TableModels.PaginatedResult<EmailModels.EmailTableRow> {
  return {
    result: apiModel.result.map((res) => {
      const row: EmailModels.EmailTableRow = {
        hasAttachments: res.hasAttachments,
        id: res.id,
        to: res.receiver,
        from: res.from,
        subject: res.subject,
        receivedDateTime: dateFromISOString(res.receivedDateTime) ?? new Date(),
      };

      return row;
    }),
    resultCount: apiModel.resultCount,
  };
}

export function projectEmailMessage(
  apiEmail: EmailApiModels.EmailMessage,
): EmailModels.EmailMessage | any {
  return {
    attachments: apiEmail.attachments,
    content: clearStyleTags(apiEmail.content!),
    from: apiEmail.from,
    id: apiEmail.id,
    sourceId: apiEmail.sourceId,
    to: apiEmail.to,
    subject: apiEmail.subject,
    isNew: false,
    receivedDateTime: apiEmail.receivedDateTime,
  };
}

export function getHealthCommissionsCouple(
  value: AgencyApiModels.HealthCommissionProductAdministration,
): HealthModels.HealthRateCommissionPairsEnum | any {
  if (
    value.linear === HealthModels.HealthRateLinearEnum.L1 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR1
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L1PR1;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L2 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR2
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L2PR2;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L7 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR3
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L7PR3;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L7 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR4
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L7PR4;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L1 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR5
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L1PR5;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L2 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR6
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L2PR6;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L2 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR7
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L2PR7;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L_24_16 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR_25_15_16
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L_24_16_PR_25_15_16;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L_24_16 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR_27_15_16
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L_24_16_PR_27_15_16;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L_24_18 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR_30_15_17
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L_24_18_PR_30_15_17;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L_24_18 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR_32_16_17
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L_24_18_PR_32_16_17;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L_24_20 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR_35_16_17
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L_24_20_PR_35_16_17;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.L_24_20 &&
    value.withholding === HealthModels.HealthRateWithholdingEnum.PR_37_15_17
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.L_24_20_PR_37_15_17;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.FIDELITY_L_24_16 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_32_10_10
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_16_PR_32_10;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.FIDELITY_L_24_16 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_35_10_10
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_16_PR_35_10;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.FIDELITY_L_24_18 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_37_10_10
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_18_PR_37_10;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.FIDELITY_L_24_18 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_40_10_10
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_18_PR_40_10;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.FIDELITY_L_24_20 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_42_10_10
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_20_PR_42_10;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.FIDELITY_L_24_20 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_45_10_10
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_20_PR_45_10;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L1 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR1_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL1PR1;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L1 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR5_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL1PR5;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L2 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR2_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR2;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L2 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR6_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR6;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L2 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR7_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR7;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L7 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR3_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL7PR3;
  } else if (
    value.linear === HealthModels.HealthRateLinearEnum.ACHEEL_L7 &&
    value.withholding ===
      HealthModels.HealthRateWithholdingEnum.ACHEEL_PR4_50_15
  ) {
    return HealthModels.HealthRateCommissionPairsEnum.AcheelL7PR4;
  } else {
    return null;
  }
}

export class AgencyProjector {
  public static agencyDetailsResponse(
    apiModel: AgencyApiModels.AgencyDetailsResponse,
  ): AgencyModels.AgencyDetails | any {
    const { waitingMetrics } = apiModel;
    return {
      attestationUrl: apiModel.attestationUrl,
      address: apiModel.address,
      agencyStatus: apiModel.status,
      headOfAgency: apiModel.headOfAgency,
      publicId: apiModel.publicId,
      headOffice: {
        name: apiModel.name,
        companyType: apiModel.companyType,
        numSiret: apiModel.numSiret,
        numSiren: apiModel.numSiren,
      },
      headOfficeLegal: {
        turnover: apiModel.turnover,
        companyCreationDate: apiModel.companyCreationDate,
        outletCount: apiModel.outletCount,
        employeeCount: apiModel.employeeCount,
      },
      productAdministration: apiModel.productAdministration
        ? {
            ropRates: apiModel.productAdministration.ropRates,
            autoRates: apiModel.productAdministration.autoRates,
            productRights: apiModel.productAdministration.productRights,
            infinityCommission: apiModel.productAdministration
              .infinityCommission
              ? {
                  withHoldingMonthlyThreshold:
                    apiModel.productAdministration.infinityCommission
                      .withHoldingMonthlyThreshold,
                  commissions: getHealthCommissionsCouple(
                    apiModel.productAdministration.infinityCommission,
                  ),
                }
              : null,
            fidelityCommission: apiModel.productAdministration
              .fidelityCommission
              ? {
                  withHoldingMonthlyThreshold:
                    apiModel.productAdministration.fidelityCommission
                      .withHoldingMonthlyThreshold,
                  commissions: getHealthCommissionsCouple(
                    apiModel.productAdministration.fidelityCommission,
                  ),
                }
              : null,
            acheelCommission: apiModel.productAdministration.acheelCommission
              ? {
                  withHoldingMonthlyThreshold:
                    apiModel.productAdministration.acheelCommission
                      .withHoldingMonthlyThreshold,
                  commissions: getHealthCommissionsCouple(
                    apiModel.productAdministration.acheelCommission,
                  ),
                }
              : null,
          }
        : null,
      mainBroker: apiModel.mainBroker
        ? {
            ...apiModel.mainBroker,
            brokerLastName: apiModel.mainBroker.brokerLastName,
            brokerFirstName: apiModel.mainBroker.brokerFirstName,
          }
        : null,
      waitingDocuments:
        waitingMetrics != null ? waitingMetrics.fragments : null,
      fidelityWithholdingMetrics: apiModel.fidelityWithholdingMetrics,
      acheelWithholdingMetrics: apiModel.acheelWithholdingMetrics,
      infinityWithholdingMetrics: apiModel.infinityWithholdingMetrics,
      ropWithholdingMetrics: apiModel.ropWithholdingMetrics,
      canValidateContract:
        waitingMetrics != null ? waitingMetrics.canValidateContract : null,
      retentions: apiModel.agencyRetentionHealthServiceModels,
      specificities: apiModel.agencySpecificitiesHealthServiceModels,
      healthSettings: apiModel.healthSettings,
      minInsertDate: apiModel.minInsertDate,
      autoSettings: apiModel.autoSettings,
    };
  }

  public static patchAgencyRequest(
    model: AgencyModels.ProductAdministrationForm,
  ): AgencyApiModels.PatchAgencyRequest | any {
    return {
      productAdministration: {
        ropRates: model.ropRates,
        autoRates: model.autoRates
          ? {
              ...model.autoRates,
            }
          : null,
        productRights: model.productRights,
        infinityCommission: model.infinityCommission
          ? {
              withHoldingMonthlyThreshold:
                model.infinityCommission.withHoldingMonthlyThreshold,
              linear: getLinearCommissions(
                model.infinityCommission.commissions,
              ),
              withholding: getWithholdingCommissions(
                model.infinityCommission.commissions,
              ),
            }
          : null,
        fidelityCommission: model.fidelityCommission
          ? {
              withHoldingMonthlyThreshold:
                model.fidelityCommission.withHoldingMonthlyThreshold,
              linear: getLinearCommissions(
                model.fidelityCommission.commissions,
              ),
              withholding: getWithholdingCommissions(
                model.fidelityCommission.commissions,
              ),
            }
          : null,
        acheelCommission: model.acheelCommission
          ? {
              withHoldingMonthlyThreshold:
                model.acheelCommission.withHoldingMonthlyThreshold,
              linear: getLinearCommissions(model.acheelCommission.commissions),
              withholding: getWithholdingCommissions(
                model.acheelCommission.commissions,
              ),
            }
          : null,
      },
    };
  }
}

export function getLinearCommissions(
  value: HealthModels.HealthRateCommissionPairsEnum,
): HealthModels.HealthRateLinearEnum | null {
  if (value === HealthModels.HealthRateCommissionPairsEnum.L1PR1) {
    return HealthModels.HealthRateLinearEnum.L1;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L2PR2) {
    return HealthModels.HealthRateLinearEnum.L2;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L7PR4) {
    return HealthModels.HealthRateLinearEnum.L7;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L7PR3) {
    return HealthModels.HealthRateLinearEnum.L7;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L1PR5) {
    return HealthModels.HealthRateLinearEnum.L1;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L2PR6) {
    return HealthModels.HealthRateLinearEnum.L2;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L2PR7) {
    return HealthModels.HealthRateLinearEnum.L2;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_16_PR_25_15_16
  ) {
    return HealthModels.HealthRateLinearEnum.L_24_16;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_16_PR_27_15_16
  ) {
    return HealthModels.HealthRateLinearEnum.L_24_16;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_18_PR_30_15_17
  ) {
    return HealthModels.HealthRateLinearEnum.L_24_18;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_18_PR_32_16_17
  ) {
    return HealthModels.HealthRateLinearEnum.L_24_18;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_20_PR_35_16_17
  ) {
    return HealthModels.HealthRateLinearEnum.L_24_20;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_20_PR_37_15_17
  ) {
    return HealthModels.HealthRateLinearEnum.L_24_20;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_16_PR_32_10
  ) {
    return HealthModels.HealthRateLinearEnum.FIDELITY_L_24_16;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_16_PR_35_10
  ) {
    return HealthModels.HealthRateLinearEnum.FIDELITY_L_24_16;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_18_PR_37_10
  ) {
    return HealthModels.HealthRateLinearEnum.FIDELITY_L_24_18;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_18_PR_40_10
  ) {
    return HealthModels.HealthRateLinearEnum.FIDELITY_L_24_18;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_20_PR_42_10
  ) {
    return HealthModels.HealthRateLinearEnum.FIDELITY_L_24_20;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_20_PR_45_10
  ) {
    return HealthModels.HealthRateLinearEnum.FIDELITY_L_24_20;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL1PR1) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L1;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL1PR5) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L1;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR2) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L2;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR6) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L2;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR7) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L2;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL7PR3) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L7;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL7PR4) {
    return HealthModels.HealthRateLinearEnum.ACHEEL_L7;
  } else {
    return null;
  }
}

export function getWithholdingCommissions(
  value: HealthModels.HealthRateCommissionPairsEnum,
): HealthModels.HealthRateWithholdingEnum | null {
  if (value === HealthModels.HealthRateCommissionPairsEnum.L1PR1) {
    return HealthModels.HealthRateWithholdingEnum.PR1;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L2PR2) {
    return HealthModels.HealthRateWithholdingEnum.PR2;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L7PR4) {
    return HealthModels.HealthRateWithholdingEnum.PR4;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L7PR3) {
    return HealthModels.HealthRateWithholdingEnum.PR3;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L1PR5) {
    return HealthModels.HealthRateWithholdingEnum.PR5;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L2PR6) {
    return HealthModels.HealthRateWithholdingEnum.PR6;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.L2PR7) {
    return HealthModels.HealthRateWithholdingEnum.PR7;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_16_PR_25_15_16
  ) {
    return HealthModels.HealthRateWithholdingEnum.PR_25_15_16;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_16_PR_27_15_16
  ) {
    return HealthModels.HealthRateWithholdingEnum.PR_27_15_16;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_18_PR_30_15_17
  ) {
    return HealthModels.HealthRateWithholdingEnum.PR_30_15_17;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_18_PR_32_16_17
  ) {
    return HealthModels.HealthRateWithholdingEnum.PR_32_16_17;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_20_PR_35_16_17
  ) {
    return HealthModels.HealthRateWithholdingEnum.PR_35_16_17;
  } else if (
    value === HealthModels.HealthRateCommissionPairsEnum.L_24_20_PR_37_15_17
  ) {
    return HealthModels.HealthRateWithholdingEnum.PR_37_15_17;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_16_PR_32_10
  ) {
    return HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_32_10_10;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_16_PR_35_10
  ) {
    return HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_35_10_10;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_18_PR_37_10
  ) {
    return HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_37_10_10;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_18_PR_40_10
  ) {
    return HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_40_10_10;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_20_PR_42_10
  ) {
    return HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_42_10_10;
  } else if (
    value ===
    HealthModels.HealthRateCommissionPairsEnum.FidelityL_24_20_PR_45_10
  ) {
    return HealthModels.HealthRateWithholdingEnum.FIDELITY_PR_45_10_10;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL1PR1) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR1_50_15;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL1PR5) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR5_50_15;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR2) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR2_50_15;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR6) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR6_50_15;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL2PR7) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR7_50_15;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL7PR3) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR3_50_15;
  } else if (value === HealthModels.HealthRateCommissionPairsEnum.AcheelL7PR4) {
    return HealthModels.HealthRateWithholdingEnum.ACHEEL_PR4_50_15;
  } else {
    return null;
  }
}
